import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, Observable, of } from 'rxjs';
import { catchError, debounceTime, exhaustMap, groupBy, map, mergeMap, switchMap } from 'rxjs/operators';

import * as BookmarkProblemListActions from '../actions/bookmark-problem-list.actions';
import { FirebaseCallableFunctionError } from '../errors/firebase-callable-function-error';
import { BookmarkProblemListService } from '../services/bookmark-problem-list.service';
import { dispatchAppError, dispatchInfoMessageWithoutDuplicates } from '../actions/core.actions';
import { FirestoreError } from '../errors/firestore-error';
import { AppError } from '../errors/app-error';
import { Action } from '@ngrx/store';
import { ToggleProblemToFromBookmarkProblemListRequest } from '../models/add-problem-to-bookmark-problem-list';

@Injectable()
export class BookmarkProblemListEffects {
  private LOG_SOURCE = this.constructor.name;

  findMyBookmarkProblemLists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkProblemListActions.findMyBookmarkProblemLists),
      switchMap(action =>
        this.bookmarkProblemListService.findMyBookmarkProblemLists(action.userId, action.subjectIds).pipe(
          map(bookmarkProblemLists => BookmarkProblemListActions.findMyBookmarkProblemListsSuccess({ bookmarkProblemLists })),
          catchError(e => {
            const error = e instanceof AppError ? e : FirestoreError.from(e);
            return of(BookmarkProblemListActions.findMyBookmarkProblemListsFailure(), dispatchAppError({ source: this.LOG_SOURCE, error }));
          })
        )
      )
    )
  );
  findSharedBookmarkProblemLists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkProblemListActions.findSharedBookmarkProblemLists),
      switchMap(action =>
        this.bookmarkProblemListService.findSharedBookmarkProblemLists(action.schoolId, action.userId).pipe(
          map(sharedBookmarkProblemLists =>
            BookmarkProblemListActions.findSharedBookmarkProblemListsSuccess({ sharedBookmarkProblemLists })
          ),
          catchError(e => {
            const error = e instanceof AppError ? e : FirestoreError.from(e);
            return of(
              BookmarkProblemListActions.findSharedBookmarkProblemListsFailure(),
              dispatchAppError({ source: this.LOG_SOURCE, error })
            );
          })
        )
      )
    )
  );
  findBookmarkProblemListProblemIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkProblemListActions.findBookmarkProblemListProblemIds),
      switchMap(action =>
        this.bookmarkProblemListService.findBookmarkProblemListProblemIds(action.bookmarkProblemListId).pipe(
          map(bookmarkProblemListProblemIds =>
            BookmarkProblemListActions.findBookmarkProblemListProblemIdsSuccess({ bookmarkProblemListProblemIds })
          ),
          catchError(e =>
            of(
              BookmarkProblemListActions.findBookmarkProblemListProblemIdsFailure(),
              dispatchAppError({ source: this.LOG_SOURCE, error: FirebaseCallableFunctionError.from(e) })
            )
          )
        )
      )
    )
  );
  createBookmarkProblemList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkProblemListActions.createBookmarkProblemList),
      switchMap(action =>
        this.bookmarkProblemListService.createBookmarkProblemList(action.subjectId, action.name, action.visibility).pipe(
          map(response => BookmarkProblemListActions.createBookmarkProblemListSuccess({ response })),
          catchError(e => of(BookmarkProblemListActions.createBookmarkProblemListFailure({ error: FirebaseCallableFunctionError.from(e) })))
        )
      )
    )
  );
  updateBookmarkProblemList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkProblemListActions.updateBookmarkProblemList),
      switchMap(action =>
        this.bookmarkProblemListService.updateBookmarkProblemList(action.id, action.name, action.visibility).pipe(
          map(response => BookmarkProblemListActions.updateBookmarkProblemListSuccess({ response })),
          catchError(e => of(BookmarkProblemListActions.updateBookmarkProblemListFailure({ error: FirebaseCallableFunctionError.from(e) })))
        )
      )
    )
  );
  copyBookmarkProblemList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkProblemListActions.copyBookmarkProblemList),
      switchMap(action =>
        this.bookmarkProblemListService.copyBookmarkProblemList(action.bookmarkProblemListId).pipe(
          map(response => BookmarkProblemListActions.copyBookmarkProblemListSuccess({ response })),
          catchError(e => of(BookmarkProblemListActions.copyBookmarkProblemListFailure({ error: FirebaseCallableFunctionError.from(e) })))
        )
      )
    )
  );
  deleteBookmarkProblemList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkProblemListActions.deleteBookmarkProblemList),
      switchMap(action =>
        this.bookmarkProblemListService.deleteBookmarkProblemList(action.id).pipe(
          map(response => BookmarkProblemListActions.deleteBookmarkProblemListSuccess({ response })),
          catchError(e => of(BookmarkProblemListActions.deleteBookmarkProblemListFailure({ error: FirebaseCallableFunctionError.from(e) })))
        )
      )
    )
  );

  // 複数アクション(チェックボックスのon/offのクリック)に対してひとつのAPIだけを呼び出す
  toggleProblemToFromBookmarkProblemList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkProblemListActions.toggleProblemToFromBookmarkProblemList),
      groupBy(action => `${action.listId}-${action.problemId}`),
      mergeMap(group$ =>
        group$.pipe(
          debounceTime(300), // 0.3秒以内に複数のアクションが呼び出された場合、最後のアクションのみを取得
          switchMap(action => this.handleBookmarkToggle(action))
        )
      )
    )
  );

  private handleBookmarkToggle(action: ToggleProblemToFromBookmarkProblemListRequest): Observable<Action> {
    return action.checked
      ? // 最終的な意図がお気に入りリストへの問題の追加の場合
        this.bookmarkProblemListService.addProblemToBookmarkProblemList(action.listId, action.problemId).pipe(
          map(response => BookmarkProblemListActions.addProblemToBookmarkProblemListSuccess({ response })),
          catchError(e =>
            of(
              BookmarkProblemListActions.addProblemToBookmarkProblemListFailure({
                error: FirebaseCallableFunctionError.from(e)
              })
            )
          )
        )
      : // 最終的な意図がお気に入りリストから問題の削除の場合
        this.bookmarkProblemListService.deleteProblemFromBookmarkProblemList(action.listId, action.problemId).pipe(
          map(response =>
            BookmarkProblemListActions.deleteProblemFromBookmarkProblemListSuccess({
              response,
              isListIdMatched: action.isListIdMatched ? action.isListIdMatched : false,
              problemId: action.problemId
            })
          ),
          catchError(e =>
            of(
              BookmarkProblemListActions.deleteProblemFromBookmarkProblemListFailure({
                error: FirebaseCallableFunctionError.from(e)
              })
            )
          )
        );
  }

  dispatchToggleProblemResultMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BookmarkProblemListActions.addProblemToBookmarkProblemListSuccess,
        BookmarkProblemListActions.addProblemToBookmarkProblemListFailure,
        BookmarkProblemListActions.deleteProblemFromBookmarkProblemListSuccess,
        BookmarkProblemListActions.deleteProblemFromBookmarkProblemListFailure
      ),
      exhaustMap(action => {
        return of(
          dispatchInfoMessageWithoutDuplicates({
            message:
              action.type === BookmarkProblemListActions.addProblemToBookmarkProblemListFailure.type ||
              action.type === BookmarkProblemListActions.deleteProblemFromBookmarkProblemListFailure.type
                ? '更新中にエラーが発生しました'
                : '更新しました'
          })
        );
      })
    )
  );

  addProblemCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkProblemListActions.addProblemCount),
      map(({ listId }) => {
        console.log(`該当のお気に入りリストの問題数が加算されました: ${listId}`);
        return BookmarkProblemListActions.addProblemCountSuccess();
      })
    )
  );

  subtractProblemCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkProblemListActions.subtractProblemCount),
      map(({ listId }) => {
        console.log(`該当のお気に入りリストの問題数が減算されました: ${listId}`);
        return BookmarkProblemListActions.subtractProblemCountSuccess();
      })
    )
  );

  addBookmarkProblemList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkProblemListActions.addBookmarkProblemList),
      map(({ bookmarkProblemList }) => {
        console.log(`bookmarkProblemListsステートに${bookmarkProblemList.id}が追加されました`);
        return BookmarkProblemListActions.addBookmarkProblemListSuccess();
      })
    )
  );

  updateBookmarkProblemListId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkProblemListActions.updateBookmarkProblemListId),
      map(({ tempId, realId }) => {
        console.log(`bookmarkProblemListsステートの${tempId}が${realId}に置き換えられました`);
        return BookmarkProblemListActions.updateBookmarkProblemListIdSuccess();
      })
    )
  );

  deleteBookmarkProblemListOnState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkProblemListActions.deleteBookmarkProblemListOnState),
      map(({ tempId }) => {
        console.log(`bookmarkProblemListsステートから${tempId}のリストが削除されました`);
        return BookmarkProblemListActions.deleteBookmarkProblemListOnStateSuccess();
      })
    )
  );

  findBookmarkProblemListProblems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkProblemListActions.findBookmarkProblemListProblems),
      switchMap(action =>
        this.bookmarkProblemListService.findBookmarkProblemListProblems(action.bookmarkProblemListId).pipe(
          map(problems => BookmarkProblemListActions.findBookmarkProblemListProblemsSuccess({ problems })),
          catchError(e =>
            of(
              BookmarkProblemListActions.findBookmarkProblemListProblemsFailure(),
              dispatchAppError({ source: this.LOG_SOURCE, error: FirebaseCallableFunctionError.from(e) })
            )
          )
        )
      )
    )
  );

  reorderBookmarkProblemListProblems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkProblemListActions.reorderBookmarkProblemListProblems),
      debounceTime(1000), // 1秒以内に複数のアクションが呼び出された場合、最後のアクションのみを取得
      switchMap(action =>
        from(this.bookmarkProblemListService.reorderBookmarkProblemListProblems(action.bookmarkProblemListId, action.problemIds)).pipe(
          map(response =>
            BookmarkProblemListActions.reorderBookmarkProblemListProblemsSuccess({ response, problemIds: action.problemIds })
          ),
          catchError(error =>
            of(
              BookmarkProblemListActions.reorderBookmarkProblemListProblemsFailure(),
              dispatchAppError({ source: this.LOG_SOURCE, error: FirebaseCallableFunctionError.from(error) })
            )
          )
        )
      )
    )
  );

  findBookmarkProblemListProblemImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookmarkProblemListActions.findBookmarkProblemListProblemImages),
      switchMap(action =>
        this.bookmarkProblemListService.findBookmarkProblemListProblemImages(action.bookmarkProblemListId).pipe(
          map(problems => BookmarkProblemListActions.findBookmarkProblemListProblemImagesSuccess({ problems })),
          catchError(e =>
            of(
              BookmarkProblemListActions.findBookmarkProblemListProblemImagesFailure(),
              dispatchAppError({ source: this.LOG_SOURCE, error: FirebaseCallableFunctionError.from(e) })
            )
          )
        )
      )
    )
  );

  constructor(private actions$: Actions, private bookmarkProblemListService: BookmarkProblemListService, private snackBar: MatSnackBar) {}
}
